import { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { trackAdClick } from "@/lib/adTracking";

interface SponsoredVideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  title: string;
  /** Sponsored ad id — when provided, opening the modal counts as a click. */
  adId?: string | null;
}

/**
 * Reusable 9:16 YouTube video modal used for sponsored / "venue story" videos.
 * Same look & feel as the homepage VenueStoryPreview spotlight.
 */
export const SponsoredVideoModal = ({
  open,
  onOpenChange,
  videoId,
  title,
  adId,
}: SponsoredVideoModalProps) => {
  useEffect(() => {
    if (open && adId) trackAdClick("sponsored", adId);
  }, [open, adId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[360px] p-0 bg-black border-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Watch the sponsored video</DialogDescription>
        </VisuallyHidden>
        <AspectRatio ratio={9 / 16}>
          <iframe
            src={open ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : ""}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title}
          />
        </AspectRatio>
      </DialogContent>
    </Dialog>
  );
};
